import PlanningSection from './PlanningSection';
import PlanningStepper from './PlanningStepper';
import { PLANNING_PHASES } from './USCGPage';
import { useIncident, useIncidentPermissions } from '../hooks/incidentHooks';
import { useMemo, useRef, useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import classNames from 'classnames';
import { registerIncidentLifecycleTourApi } from 'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTourBridge';
import {
  INCIDENT_TOUR_CONTINUE_KEY,
  INCIDENT_TOUR_PARAM,
  incidentTourArchiveHref,
  wantsIncidentTour,
} from 'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTourSteps';
import { registerFocTransitionTourApi } from 'components/MapPage/focTransitionTour/focTransitionTourBridge';
import {
  FOC_TRANSITION_TOUR_CONTINUE_KEY,
  FOC_TRANSITION_TOUR_PARAM,
} from 'components/MapPage/focTransitionTour/focTransitionTourSteps';
import { registerFocPlanningPTourApi } from 'components/MapPage/focPlanningPTour/focPlanningPTourBridge';
import {
  findFocPlanningPTourEntryByParam,
  FOC_PLANNING_P_TOUR_ENTRIES,
} from 'components/MapPage/focPlanningPTour/focPlanningPTourRegistry';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import { useICSContext } from './ICSContext';
import {
  CorporateViewTabGroup,
  CorporateViewTabs,
} from 'components/MapPage/CorporateViewTabGroup';
import { UscgMapLayout } from 'components/MapPage/UscgMapLayout';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { Button } from '@/components/ui/button';
import type { FormsView } from 'utils/iapFormsView';
import { ChevronRight } from 'lucide-react';

const STEPPER_OPEN_STORAGE_KEY = 'planning-stepper-open';

const isDualSurfaceOpenTab = (openTab: string | null) =>
  openTab === CorporateViewTabs.Reports ||
  openTab === CorporateViewTabs.Resources ||
  openTab === CorporateViewTabs.Actions;

function readStepperOpenPreference(): boolean {
  try {
    const stored = sessionStorage.getItem(STEPPER_OPEN_STORAGE_KEY);
    if (stored === null) return true;
    return stored === 'true';
  } catch {
    return true;
  }
}

const PlanningDashboard = ({
  updatePhase,
  updateMode,
  currentPhase,
  currentMode,
}: {
  updatePhase: (phase: number) => void;
  updateMode: (isFutureMode: string) => void;
  updateFormsView: (view: FormsView) => void;
  currentPhase: number;
  currentMode: string;
  formsView: FormsView;
}) => {
  const { isMapVisible, setIsMapVisible } = useUscgMapContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setCard } = useICSContext();
  const openTab = searchParams.get('openTab');

  const { data: incidentPermissions } = useIncidentPermissions();

  const canViewFuturePeriod =
    incidentPermissions?.['incident:viewFuturePeriod'] ?? false;

  const mainContentRef = useRef<HTMLDivElement>(null);
  const [isPlanningStepperOpen, setIsPlanningStepperOpen] = useState(
    readStepperOpenPreference
  );
  const [showAgenda, setShowAgenda] = useState(false);
  const safeCurrentPhase = Math.max(
    0,
    Math.min(
      Number.isFinite(currentPhase) ? currentPhase : 0,
      PLANNING_PHASES.length - 1
    )
  );
  const incident = useIncident();

  useEffect(() => {
    const phase = PLANNING_PHASES[safeCurrentPhase];
    if (!phase?.agenda?.length && showAgenda) {
      setShowAgenda(false);
    }
  }, [safeCurrentPhase, showAgenda]);

  // Incident Lifecycle tour: the Planning P phase runs here, stepping the
  // rail and the Forms dropdown, then hands off to settings.
  const updatePhaseRef = useRef(updatePhase);
  updatePhaseRef.current = updatePhase;
  const setCardRef = useRef(setCard);
  setCardRef.current = setCard;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const incidentIdRef = useRef(incident.data?.id);
  incidentIdRef.current = incident.data?.id;
  const wantsTour = wantsIncidentTour(
    searchParams.get(INCIDENT_TOUR_PARAM),
    'planning'
  );
  const tourStartedRef = useRef(false);
  const canHostTour = Boolean(incident.data) && canViewFuturePeriod;
  const uscgFoc = useUscgFocRequirementsFlag();
  const wantsFocTransitionTour =
    uscgFoc &&
    (searchParams.get(FOC_TRANSITION_TOUR_PARAM) === 'planning' ||
      sessionStorage.getItem(FOC_TRANSITION_TOUR_CONTINUE_KEY) === 'planning');
  const focTransitionTourStartedRef = useRef(false);
  const focPlanningPTourEntry = uscgFoc
    ? findFocPlanningPTourEntryByParam(searchParams)
    : null;
  const wantsFocPlanningPTour = Boolean(focPlanningPTourEntry);
  const focPlanningPTourStartedRef = useRef(false);

  useEffect(
    () =>
      registerIncidentLifecycleTourApi({
        setPlanningPhase: (index) => updatePhaseRef.current(index),
        setPlanningCard: (widgetName) => setCardRef.current(widgetName),
        navigateTo: (href) => navigateRef.current(href),
        resolveArchiveHref: () => {
          const id = incidentIdRef.current;
          return id ? incidentTourArchiveHref(String(id)) : null;
        },
      }),
    []
  );

  useEffect(
    () =>
      registerFocTransitionTourApi({
        getLocation: () => ({
          pathname: window.location.pathname,
          search: window.location.search,
        }),
        ensurePlanningPhase0: () => {
          updatePhaseRef.current(0);
          setIsPlanningStepperOpen(true);
        },
        showPlanningAgenda: () => setShowAgenda(true),
        selectPlanningWidget: (widgetName) => setCardRef.current(widgetName),
        navigateTo: (href) => navigateRef.current(href),
        scrollToTour: (tour) => {
          window.setTimeout(() => {
            const el = document.querySelector(`[data-tour="${tour}"]`);
            if (el instanceof HTMLElement) {
              el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 50);
        },
      }),
    []
  );

  useEffect(
    () =>
      registerFocPlanningPTourApi({
        getLocation: () => ({
          pathname: window.location.pathname,
          search: window.location.search,
        }),
        setPlanningPhase: (index) => updatePhaseRef.current(index),
        ensureStepperOpen: () => setIsPlanningStepperOpen(true),
        showPlanningAgenda: () => setShowAgenda(true),
        selectPlanningWidget: (widgetName) => setCardRef.current(widgetName),
        openMeetingDetails: () => {
          const el = document.querySelector(
            '[data-tour="planning-meeting-details"]'
          );
          if (el instanceof HTMLElement) el.click();
        },
        navigateTo: (href) => navigateRef.current(href),
        scrollToTour: (tour) => {
          window.setTimeout(() => {
            const el = document.querySelector(`[data-tour="${tour}"]`);
            if (el instanceof HTMLElement) {
              el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 50);
        },
      }),
    []
  );

  useEffect(() => {
    if (!wantsTour || tourStartedRef.current || !canHostTour) return;

    const timer = window.setTimeout(() => {
      if (tourStartedRef.current) return;
      tourStartedRef.current = true;
      sessionStorage.removeItem(INCIDENT_TOUR_CONTINUE_KEY);
      setSearchParams(
        (params) => {
          params.delete(INCIDENT_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import(
        'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTour'
      )
        .then(({ startIncidentLifecycleTour }) => {
          startIncidentLifecycleTour('planning');
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsTour, canHostTour, setSearchParams]);

  useEffect(() => {
    if (
      !wantsFocTransitionTour ||
      focTransitionTourStartedRef.current ||
      !canHostTour
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (focTransitionTourStartedRef.current) return;
      focTransitionTourStartedRef.current = true;
      sessionStorage.removeItem(FOC_TRANSITION_TOUR_CONTINUE_KEY);
      setSearchParams(
        (params) => {
          params.delete(FOC_TRANSITION_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      updatePhaseRef.current(0);
      void import('components/MapPage/focTransitionTour/focTransitionTour')
        .then(async ({ startFocTransitionTour, waitForSelector }) => {
          await waitForSelector('[data-tour="planning-stepper"]');
          startFocTransitionTour({ startId: 'trn001b' });
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsFocTransitionTour, canHostTour, setSearchParams]);

  // FOC 10.x needs the Planning-P stepper workspace, not the default Hub map.
  useEffect(() => {
    if (!wantsFocPlanningPTour) return;
    if (isMapVisible) setIsMapVisible(false);
    if (isDualSurfaceOpenTab(openTab)) {
      setSearchParams(
        (params) => {
          params.delete('openTab');
          return params;
        },
        { replace: true }
      );
    }
  }, [
    wantsFocPlanningPTour,
    isMapVisible,
    openTab,
    setIsMapVisible,
    setSearchParams,
  ]);

  useEffect(() => {
    if (
      !wantsFocPlanningPTour ||
      !focPlanningPTourEntry ||
      focPlanningPTourStartedRef.current ||
      !canHostTour
    ) {
      return;
    }
    // Wait until the map/dual-surface layout is gone so the stepper mounts.
    if (isMapVisible || isDualSurfaceOpenTab(openTab)) return;

    const entry = focPlanningPTourEntry;
    const timer = window.setTimeout(() => {
      if (focPlanningPTourStartedRef.current) return;
      focPlanningPTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          for (const tour of FOC_PLANNING_P_TOUR_ENTRIES) {
            params.delete(tour.param);
          }
          return params;
        },
        { replace: true }
      );
      updatePhaseRef.current(entry.phase - 1);
      setIsPlanningStepperOpen(true);
      void entry
        .importTour()
        .then(async ({ startTour, waitForSelector }) => {
          await waitForSelector('[data-tour="planning-stepper"]');
          startTour();
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [
    wantsFocPlanningPTour,
    focPlanningPTourEntry,
    canHostTour,
    isMapVisible,
    openTab,
    setSearchParams,
  ]);

  const showCorporateView = useMemo(
    () => isMapVisible || isDualSurfaceOpenTab(openTab),
    [isMapVisible, openTab]
  );

  const setStepperOpen = (open: boolean) => {
    setIsPlanningStepperOpen(open);
    try {
      sessionStorage.setItem(STEPPER_OPEN_STORAGE_KEY, String(open));
    } catch {
      // ignore storage failures
    }
  };

  const mainContentClasses = classNames('maincontent', {
    'iap-response__main': showCorporateView,
    'maincontent-no-padding': showCorporateView,
    'h-100': showCorporateView,
  });

  if (incident.data === undefined) {
    return null;
  }

  const resolvedIncidentId = incident.data?.id;

  return (
    <div
      className={
        showCorporateView
          ? 'd-flex flex-column h-100 w-100'
          : canViewFuturePeriod
            ? 'h-100 w-100'
            : 'd-flex flex-column w-100'
      }
      style={
        showCorporateView ? { minHeight: 0, overflow: 'hidden' } : undefined
      }
    >
      {showCorporateView ? (
        <div
          className={mainContentClasses}
          ref={mainContentRef}
          style={{ minHeight: 0, overflow: 'hidden' }}
        >
          <UscgMapLayout>
            <CorporateViewTabGroup
              ics={true}
              currentMode={currentMode}
              updateMode={updateMode}
              currentPhase={currentPhase}
            />
          </UscgMapLayout>
        </div>
      ) : canViewFuturePeriod ? (
        <div className="planning-p-workspace">
          {isPlanningStepperOpen ? (
            <PlanningStepper
              planningPhases={PLANNING_PHASES}
              currentPhase={safeCurrentPhase}
              updatePhase={updatePhase}
              isOpen
              onHide={() => setStepperOpen(false)}
              isAgendaOpen={showAgenda}
              onToggleAgenda={() => setShowAgenda((open) => !open)}
              mainContentRef={mainContentRef}
            />
          ) : (
            <div className="dark planning-stepper-rail">
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                className="planning-stepper-rail__toggle tw:rounded-none tw:border-0 tw:border-b tw:border-border tw:shadow-none"
                onClick={() => setStepperOpen(true)}
                aria-label="Expand planning stepper"
                title="Expand planning stepper"
              >
                <ChevronRight className="tw:h-4 tw:w-4" aria-hidden />
              </Button>
              <PlanningStepper
                planningPhases={PLANNING_PHASES}
                currentPhase={safeCurrentPhase}
                updatePhase={updatePhase}
                isOpen={false}
              />
            </div>
          )}
          <div className="planning-p-workspace__content" ref={mainContentRef}>
            <div className="planning-container">
              <PlanningSection
                planningPhases={PLANNING_PHASES}
                currentPhase={safeCurrentPhase}
                updatePhase={updatePhase}
                showAgenda={showAgenda}
                key={safeCurrentPhase}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className={mainContentClasses} ref={mainContentRef}>
          <div className="tw:p-4 tw:text-sm tw:text-muted-foreground">
            <p className="tw:mb-2">
              You do not have permission to edit the Working operational period
              on Planning-P.
            </p>
            {resolvedIncidentId ? (
              <Link
                to={`/iap/${resolvedIncidentId}/approved-forms`}
                className="tw:text-primary tw:underline"
              >
                View Approved ICS Forms
              </Link>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningDashboard;
